/**
 * 容园 XD 协议键值 ↔ 驱动 UI 键码（UI / 改键源盘 / 社区预览层）
 *
 * SDK 侧 `main-app/.../rongyuan/sdk/resources/keyCodes.ts` 为设备读写字符集；
 * 改键源盘扩展键见 `rongyuanProtocolExtraKeys.ts`（与协议 SPECIAL_KEYS + MOUSE_KEYS 对齐）。
 *
 * - page=0 普通键：UI 码 = HID value（与协议 Key Code Table 一致）
 * - page≠0 / Fn：UI 码 = (page<<24)|(p1<<16)|(value<<8)|p2
 * - Fn1 兼容 KEYCODE_MAP：61697 ↔ [0x0A,0x01,0x00,0x00]
 */

import {
  RONGYUAN_ALL_PROTOCOL_KEYS,
  RONGYUAN_EXTRA_KEYS_MATRIX,
} from "./rongyuanProtocolExtraKeys";
import { RONGYUAN_UI_DISABLED } from "./rongyuanXsKeyBridge";
import {
  rongyuanXdToXsUiCode,
  xsUiCodeToRongyuanXd,
} from "./rongyuanXsKeyBridge";

export {
  RONGYUAN_ALL_PROTOCOL_KEYS,
  RONGYUAN_EXTRA_KEY_SOURCES,
  RONGYUAN_PROTOCOL_EXTRA_KEYS,
  rongyuanSdkKeyCharacterToXd,
} from "./rongyuanProtocolExtraKeys";
export { RONGYUAN_UI_DISABLED } from "./rongyuanXsKeyBridge";
export type {
  RongyuanExtraKeyCategory,
  RongyuanSdkKeySource,
} from "./rongyuanProtocolExtraKeys";

export const RONGYUAN_UI_FN1 = 61697;
export const RONGYUAN_UI_FN2 = 61698;
export const RONGYUAN_UI_FN3 = 61699;

export interface RongyuanXdKeyData {
  page: number;
  param1: number;
  value: number;
  param2: number;
}

export interface RongyuanUiKeyMeta {
  uiCode: number;
  name: string;
  xd: RongyuanXdKeyData;
}

const FN_UI_TO_XD: Readonly<Record<number, RongyuanXdKeyData>> = {
  [RONGYUAN_UI_FN1]: { page: 0x0a, param1: 0x01, value: 0x00, param2: 0x00 },
  // [0x0A,0x02] 为恢复出厂（61952），[0x0A,0x03] 为锁 Win（61961），勿当作 Fn2/Fn3
};

/** page≠0 时打包为唯一 UI 键码 */
export function packRongyuanUiKeyCode(xd: RongyuanXdKeyData): number {
  if (xd.page === 0 && xd.param1 === 0 && xd.param2 === 0 && xd.value <= 0xff) {
    return xd.value;
  }
  return (
    ((xd.page & 0xff) << 24) |
    ((xd.param1 & 0xff) << 16) |
    ((xd.value & 0xff) << 8) |
    (xd.param2 & 0xff)
  );
}

const UI_CODE_TO_META = new Map<number, RongyuanUiKeyMeta>();
const XD_SIG_TO_UI = new Map<string, number>();

function xdSignature(xd: RongyuanXdKeyData): string {
  return `${xd.page}:${xd.param1}:${xd.value}:${xd.param2}`;
}

const FN_UI_LABEL: Readonly<Record<number, string>> = {
  [RONGYUAN_UI_FN1]: "Fn",
};

for (const [ui, xd] of Object.entries(FN_UI_TO_XD)) {
  const uiCode = Number(ui);
  UI_CODE_TO_META.set(uiCode, {
    uiCode,
    name: FN_UI_LABEL[uiCode] ?? "Fn",
    xd,
  });
  XD_SIG_TO_UI.set(xdSignature(xd), uiCode);
}

for (const meta of RONGYUAN_ALL_PROTOCOL_KEYS) {
  UI_CODE_TO_META.set(meta.uiCode, meta);
  XD_SIG_TO_UI.set(xdSignature(meta.xd), meta.uiCode);
}

export function unpackRongyuanUiKeyCode(uiCode: number): RongyuanXdKeyData {
  if (uiCode === RONGYUAN_UI_DISABLED) {
    return { page: 0, param1: 0, value: 0, param2: 0 };
  }

  const fnXd = FN_UI_TO_XD[uiCode];
  if (fnXd) return { ...fnXd };

  const fromXs = xsUiCodeToRongyuanXd(uiCode);
  if (fromXs) return fromXs;

  if (uiCode >= 0x01000000) {
    return {
      page: (uiCode >>> 24) & 0xff,
      param1: (uiCode >>> 16) & 0xff,
      value: (uiCode >>> 8) & 0xff,
      param2: uiCode & 0xff,
    };
  }

  return { page: 0, param1: 0, value: uiCode, param2: 0 };
}

/** 容园 XD 高级键 page（协议 Key Code Table：Snap / MDS / MT） */
export const RONGYUAN_AK_PAGE_SNAP = 0x16;
export const RONGYUAN_AK_PAGE_MDS = 0x17;
export const RONGYUAN_AK_PAGE_MT = 0x18;
export const RONGYUAN_ADVANCED_KEY_PAGES = [
  RONGYUAN_AK_PAGE_SNAP,
  RONGYUAN_AK_PAGE_MDS,
  RONGYUAN_AK_PAGE_MT,
] as const;

/** UI 键码 → 协议单字节 HID（高级键槽内 Keycode 字段） */
export function uiKeyCodeToProtocolByte(uiCode: number): number {
  const code = Number(uiCode);
  if (!Number.isFinite(code)) return 0;
  const xd = unpackRongyuanUiKeyCode(code);
  if (xd.page === 0) {
    if (xd.value > 0) return xd.value & 0xff;
    if (xd.param1 > 0) return xd.param1 & 0xff;
    return code & 0xff;
  }
  return xd.value & 0xff;
}

/** UI 键码是否表示已占用高级键槽（page 10/13/14） */
export function isRongyuanAdvancedUiKeyCode(uiCode: number): boolean {
  if (!Number.isFinite(uiCode)) return false;
  const xd = unpackRongyuanUiKeyCode(uiCode);
  return (RONGYUAN_ADVANCED_KEY_PAGES as readonly number[]).includes(xd.page);
}

export function rongyuanXdToUiKeyCode(
  page: number,
  param1: number,
  value: number,
  param2: number
): number {
  const sig = xdSignature({ page, param1, value, param2 });
  const packed = XD_SIG_TO_UI.get(sig);
  if (packed !== undefined) return packed;

  if (page === 0 && param1 === 0 && value === 0 && param2 === 0) {
    return RONGYUAN_UI_DISABLED;
  }

  if (page === 0x0a && value === 0 && param1 === 0x01) return RONGYUAN_UI_FN1;

  return rongyuanXdToXsUiCode(page, param1, value, param2);
}

export function getRongyuanUiKeyMeta(
  uiCode: number
): RongyuanUiKeyMeta | undefined {
  return UI_CODE_TO_META.get(uiCode);
}

export const RONGYUAN_MACRO_UI_BASE = 62720;

export function rongyuanMacroUiCodeToId(uiCode: number): number {
  return uiCode - RONGYUAN_MACRO_UI_BASE;
}

export function rongyuanMacroIdToUiCode(macroId: number): number {
  return RONGYUAN_MACRO_UI_BASE + macroId;
}

/**
 * 容园协议 Macro Key（page=9）Byte1 Type：
 * 0=repeat，1=always，2=release（见协议 Macro Key 表）
 */
export function rongyuanMacroUiModeToKeyType(uiMode: number): number {
  const mode = Number(uiMode);
  if (mode === 1) return 1;
  if (mode === 2) return 2;
  return 0;
}

/** MacroPlayMode 枚举(0x01/0x02/0x03) → UI 模式 id(0/1/2) */
export function rongyuanMacroPlayModeToUiMode(playMode: number): number {
  const v = Number(playMode);
  if (v === 0x01) return 1;
  if (v === 0x03) return 2;
  return 0;
}

/** 构造容园宏按键 4 字节：[0x09, Type, macroId, 0x00] */
export function buildRongyuanMacroKeyBytes(
  macroId: number,
  uiMode: number = 0
): RongyuanXdKeyData {
  return {
    page: 9,
    param1: rongyuanMacroUiModeToKeyType(uiMode),
    value: macroId & 0xff,
    param2: 0,
  };
}

export function getRongyuanUiKeyLabel(uiCode: number): string {
  if (uiCode === RONGYUAN_UI_DISABLED) return "";
  if (
    uiCode >= RONGYUAN_MACRO_UI_BASE &&
    uiCode <= RONGYUAN_MACRO_UI_BASE + 15
  ) {
    return `M${rongyuanMacroUiCodeToId(uiCode)}`;
  }
  const meta = getRongyuanUiKeyMeta(uiCode);
  if (meta) return meta.name;
  if (uiCode === RONGYUAN_UI_FN1) return "Fn";
  if (uiCode === RONGYUAN_UI_FN2) return "Fn2";
  return "";
}

export function buildRongyuanUnifiedKeyConfig(input: {
  keyIndex: number;
  uiCode: number;
  fallbackName?: string;
}): {
  keyIndex: number;
  keyCode: number;
  keyName: string;
  keyType: string;
  macroId?: number;
  /** 容园宏按键播放模式 UI id（0/1/2），写入按键矩阵 Byte1 */
  macroUiMode?: number;
  keyParams?: {
    page: string;
    param1: number;
    param2: number;
    param3: number;
  };
} {
  const uiCode = Number(input.uiCode);
  const label =
    getRongyuanUiKeyLabel(uiCode) ||
    input.fallbackName ||
    `Key${input.keyIndex}`;

  const config: {
    keyIndex: number;
    keyCode: number;
    keyName: string;
    keyType: string;
    macroId?: number;
    keyParams?: {
      page: string;
      param1: number;
      param2: number;
      param3: number;
    };
  } = {
    keyIndex: input.keyIndex,
    keyCode: uiCode,
    keyName: label,
    keyType: "key",
  };

  if (
    uiCode >= RONGYUAN_MACRO_UI_BASE &&
    uiCode <= RONGYUAN_MACRO_UI_BASE + 15
  ) {
    const macroId = rongyuanMacroUiCodeToId(uiCode);
    config.keyType = "MACRO";
    config.macroId = macroId;
    config.keyName = `M${macroId}`;
    return config;
  }

  if (uiCode === RONGYUAN_UI_DISABLED) {
    config.keyType = "DISABLED";
    config.keyParams = {
      page: "0",
      param1: 0,
      param2: 0,
      param3: 0,
    };
    return config;
  }

  const xd = unpackRongyuanUiKeyCode(uiCode);
  if (xd.page === 1) config.keyType = "MOUSE";
  else if (xd.page === 3) config.keyType = "MULTIMEDIA";
  else if (xd.page === 9) config.keyType = "MACRO";
  else if (
    (RONGYUAN_ADVANCED_KEY_PAGES as readonly number[]).includes(xd.page)
  ) {
    config.keyType = "ADVANCED";
  }
  config.keyParams = {
    page: String(xd.page),
    param1: xd.param1,
    param2: xd.param2,
    param3: 0,
  };
  return config;
}

/** 改键源盘：按容园协议分类的 UI 键码矩阵 */
export const EXTRA_KEYS_MATRIX_RONGYUAN = RONGYUAN_EXTRA_KEYS_MATRIX;
